import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import AdminLayout from '@/layouts/AdminLayout'
import { timeAgo } from '@/lib/formatters'

interface EmailPipeline {
  last_fetch_at: string | null
  mailbox: string | null
  emails_fetched_today: number
  total_emails: number
}

interface AgentStats {
  today_by_status: Record<string, number>
  total_sessions: number
  recent_failures: {
    id: string
    reference_value: string | null
    error: string | null
    created_at: string
  }[]
}

interface JobQueue {
  available: boolean
  pending?: number
  scheduled?: number
  failed?: number
  recent_failures?: {
    id: string
    job_class: string
    error: string | null
    created_at: string
  }[]
}

interface Services {
  database: { status: string }
  pdf_sidecar: { status: string }
}

interface Props {
  email_pipeline: EmailPipeline
  agent_stats: AgentStats
  job_queue: JobQueue
  services: Services
}

function StatusDot({ up }: { up: boolean }) {
  return (
    <span className={`inline-block h-2 w-2 rounded-full ${up ? 'bg-green-500' : 'bg-red-500'}`} />
  )
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  )
}

export default function SystemStatus({ email_pipeline, agent_stats, job_queue, services }: Props) {
  const todayTotal = Object.values(agent_stats.today_by_status).reduce((a, b) => a + b, 0)
  const todayFailed = agent_stats.today_by_status['failed'] || 0
  const todayCompleted = agent_stats.today_by_status['completed'] || 0

  return (
    <>
      <header className="flex h-14 items-center border-b px-8">
        <h1 className="text-sm font-semibold">System Status</h1>
      </header>

      <div className="space-y-8 overflow-y-auto p-8">
        {/* Services */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Services</h2>
          <div className="flex gap-4">
            {Object.entries(services).map(([name, { status }]) => (
              <Card key={name} className="flex items-center gap-2.5 px-4 py-3">
                <StatusDot up={status === 'up'} />
                <span className="text-[13px] font-medium capitalize">{name.replace('_', ' ')}</span>
                <Badge
                  variant="secondary"
                  size="sm"
                  className={`ml-1 ${
                    status === 'up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {status}
                </Badge>
              </Card>
            ))}
          </div>
        </section>

        {/* Email Pipeline */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email Pipeline</h2>
          <div className="grid grid-cols-3 gap-4">
            <StatCard
              label="Last IMAP Fetch"
              value={timeAgo(email_pipeline.last_fetch_at)}
              sub={email_pipeline.mailbox || undefined}
            />
            <StatCard label="Fetched Today" value={email_pipeline.emails_fetched_today} />
            <StatCard label="Total Emails" value={email_pipeline.total_emails.toLocaleString()} />
          </div>
        </section>

        {/* Agent Processing */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Agent Processing</h2>
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Sessions Today" value={todayTotal} />
            <StatCard label="Completed" value={todayCompleted} />
            <StatCard label="Failed" value={todayFailed} />
            <StatCard label="Total (All Time)" value={agent_stats.total_sessions.toLocaleString()} />
          </div>

          {agent_stats.recent_failures.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">Recent Failures</h3>
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30 text-left">
                      <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Reference</th>
                      <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Error</th>
                      <th className="px-4 py-2 text-xs font-medium text-muted-foreground">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agent_stats.recent_failures.map((f) => (
                      <tr key={f.id} className="border-b last:border-0">
                        <td className="px-4 py-2 text-[13px] font-mono">{f.reference_value || '—'}</td>
                        <td className="px-4 py-2 text-[13px] text-red-600 truncate max-w-[300px]">{f.error || '—'}</td>
                        <td className="px-4 py-2 text-[13px] text-muted-foreground">{timeAgo(f.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* Job Queue */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Job Queue</h2>
          {job_queue.available ? (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Pending" value={job_queue.pending || 0} />
              <StatCard label="Scheduled" value={job_queue.scheduled || 0} />
              <StatCard label="Failed" value={job_queue.failed || 0} />
            </div>
          ) : (
            <Card className="p-4">
              <p className="text-[13px] text-muted-foreground">
                Solid Queue tables not available in this environment. Job queue monitoring is available in production.
              </p>
            </Card>
          )}
        </section>
      </div>
    </>
  )
}

SystemStatus.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
